import { createSlice } from '@reduxjs/toolkit';
import logger from '@/lib/logger';
import type { SuggestedAction } from './aiAnalysisTypes';
import { splitActionsByDisplayCaps } from './aiAnalysisHelpers';
import { aiAnalysisReducers } from './aiAnalysisReducers';
import { initialState } from './aiAnalysisState';
import { deduplicateAiAnalysisData, geocodePendingLocations } from './aiAnalysisThunks';

export { QUICKNOTE_OPTIONS } from './aiAnalysisState';
const aiAnalysisSlice = createSlice({
  name: 'aiAnalysis',
  initialState,
  reducers: aiAnalysisReducers,
  extraReducers: (builder) => {
    // Handle successful deduplication
    builder.addCase(deduplicateAiAnalysisData.fulfilled, (state, action) => {
      const { suggestedActions, tasks } = action.payload;

      // Update suggested actions if deduplicated
      if (suggestedActions) {
        // CRITICAL: Sort by priority BEFORE splitting into visible/queued
        // Lower priority number = higher priority (1=critical, 50=baseline, 100=deferred)
        const sortedActions = [...suggestedActions].sort((a, b) =>
          (a.priority ?? 50) - (b.priority ?? 50)
        );
        // Split into active (max 15) and queued with type caps
        const { visible: activeActions, queued: queuedActions } = splitActionsByDisplayCaps(sortedActions);

        // Initialize timestamps for all actions (enables 60s stability lock)
        const now = Date.now();
        suggestedActions.forEach((item: SuggestedAction) => {
          if (!(item.id in state.lastPriorityChangeTimes)) {
            state.lastPriorityChangeTimes[item.id] = now;
          }
        });

        state.suggestedActions = activeActions;
        state.queuedActions = queuedActions;

        logger.info('DeduplicationThunk', 'Applied deduplicated suggested actions', {
          active: activeActions.length,
          queued: queuedActions.length,
        });
      }

      // Update activity feed events if deduplicated
      if (tasks) {
        state.activityFeed.events = tasks;
        logger.info('DeduplicationThunk', 'Applied deduplicated tasks', {
          count: tasks.length,
        });
      }
    });

    builder.addCase(deduplicateAiAnalysisData.rejected, (state, action) => {
      logger.error('DeduplicationThunk', 'Deduplication was rejected', {
        error: action.error.message,
      });
    });

    // Handle geocoding results
    builder.addCase(geocodePendingLocations.fulfilled, (state, action) => {
      const { results } = action.payload;
      if (!results || results.length === 0) return;

      for (const result of results) {
        // Find matching location by id or name
        const index = state.entities.locations.findIndex(loc =>
          (result.id && loc.id === result.id) ||
          (result.name && loc.name === result.name)
        );
        if (index >= 0) {
          // An approximate result's formattedAddress is the LOCALITY
          // ('Etobicoke, Ontario, Canada'), not the address that was asked for.
          // Writing it over `address` would erase what the caller actually said
          // — evidence from the call — and replace it with something coarser
          // that reads as confirmed. Keep the stated address; record the
          // resolved locality alongside it.
          const isApproximate = result.isApproximate === true;
          const existing = state.entities.locations[index];

          const next: Record<string, unknown> = {
            ...existing,
            latitude: result.latitude,
            longitude: result.longitude,
            geocodingStatus: result.geocodingStatus,
            geocodingError: result.geocodingError || null,
            ...(result.jurisdiction ? { jurisdiction: result.jurisdiction } : {}),
            ...(result.formattedAddress && result.geocodingStatus === 'success'
              && !isApproximate
              ? { address: result.formattedAddress }
              : {}),
          };

          if (isApproximate) {
            next.isApproximate = true;
            next.locationPrecision = result.locationPrecision || 'locality';
            next.approximateOf = result.approximateOf ?? existing.address ?? null;
            next.approximateResolvedTo = result.formattedAddress || null;
            if (typeof result.approximateKmFromAnchor === 'number') {
              next.approximateKmFromAnchor = result.approximateKmFromAnchor;
            } else {
              delete next.approximateKmFromAnchor;
            }
          } else {
            // DELETE rather than set false/null. A later pass that resolves
            // exactly must clear the marking — but `false`/`null` are not the
            // same as absent to the entity merger: `mergeEntityValue` keeps the
            // stored value when the incoming analysis payload omits the key, and
            // `entitiesEqual` compares `JSON.stringify` — where `null` is
            // `"null"` and absent is `undefined`. Writing the cleared values
            // therefore made every geocoded location differ from every
            // subsequent payload and re-stamp its "time since change" badge
            // forever, which is the bug this file's sibling test suite exists
            // for. Verified: setting them false/null turns that suite red.
            delete next.isApproximate;
            delete next.locationPrecision;
            delete next.approximateOf;
            delete next.approximateResolvedTo;
            delete next.approximateKmFromAnchor;
          }

          state.entities.locations[index] = next as typeof existing;
        }
      }

      logger.info('GeocodingThunk', 'Applied geocoding results', {
        total: results.length,
        success: results.filter((r: any) => r.geocodingStatus === 'success').length,
        failed: results.filter((r: any) => r.geocodingStatus === 'failed').length,
      });
    });
  },
});

export const {
  reorderActions,
  forceReorderActions,
  setSuggestedActions,
  addSuggestedAction,
  recordForceGenerateAck,
  markPromptDetected,
  undoDetection,
  markOfficerAddressed,
  // Interim state management (fast detection on interim transcripts)
  updateInterimState,
  clearInterimState,
  markPromptAnswered,
  markPromptPartial,
  markPromptNegativeTier,
  appendToPartialAnswer,
  extendAnswer,
  setImprovementPatch,
  upgradePartialToComplete,
  startFadeOut,
  removePrompt,
  toggleLockPrompt,
  deprioritizePrompt,
  markPromptFailed,
  clearPromptFailed,
  forceRemovePrompt,
  followUpAction,
  restorePrompt,
  updateActionNote,
  updateActivityEventNote,
  updateCallerNote,
  setCallerUserEnteredData,
  restoreSavedCaller,
  clearCallerData,
  updateSuspectNote,
  updatePersonNote,
  updateLocationNote,
  updateObjectNote,
  updatePolicyNote,
  pinSuggestedAction,
  pinEntity,
  pinCustomNote,
  pinChat,
  pinTranscript,
  pinTPSResult,
  pinActivityEvent,
  pinCanvasAnnotation,
  setPinnedItems,
  unpinItem,
  updatePinnedItemNote,
  pinRagDocument,
  setRelatedDocumentsSnapshot,
  setRelatedDocumentsOverallSummary,
  setMapLayerConfig,
  unpinRagDocument,
  updateRagDocumentNote,
  startQuickNote,
  receiveQuickNoteAI,
  updatePendingQuickNote,
  acceptQuickNote,
  rejectQuickNote,
  cancelQuickNote,
  trackAiQuery,
  trackAiSearch,
  clearAiQueriesAndSearches,
  purgeActionById,
  resetAiAnalysis,
  hydrateAiAnalysisState,
  hydrateCanonicalActionProjection,
  clearExtractionErrors,
  setAiAnalysisPayload,
  appendAiAnalysisPayload,
  updateLastTranscriptTime,
  updateLastItemsTime,
  markActionSpoken,
  addSpeakAttempt,
  startSpeakAttempt,
  completeSpeakAttempt,
  failSpeakAttempt,
  cancelSpeakAttempt,
  setAnalysisStatus,
  setAnalysisHydrating,
  updateAnswerDetails,
} = aiAnalysisSlice.actions;

export { deduplicateAiAnalysisData, geocodePendingLocations };

export type {
  ActionSeverity,
  ActionSourceCitation,
  ActivityEvent,
  ActivityFeed,
  ActivityTranscriptEvidence,
  AiAnalysisState,
  AiQuery,
  AiSearch,
  AnswerStatus,
  AnsweredPart,
  Caller,
  ChunkSummary,
  ConflictDetails,
  Connections,
  ContradictionRecord,
  Entities,
  EntityMetadata,
  EntityRelationship,
  EntityStatus,
  ExtractionError,
  ExtractionErrors,
  ExtractionMethod,
  ImprovementPatch,
  Incident,
  IncidentSeverity,
  InterimState,
  Location,
  LocationJurisdiction,
  ObjectItem,
  Officer,
  Organization,
  OtherEntity,
  PendingQuickNote,
  Person,
  PinnedDocument,
  PinnedItem,
  PolicyAndLaw,
  QuestionPart,
  QuickNoteStatus,
  RelationshipType,
  SuggestedAction,
  Suspect,
  TranscriptEvidence,
} from './aiAnalysisTypes';

export default aiAnalysisSlice.reducer;
